import { convex } from "@/lib/convexClient";
import type { Panorama } from "@/store/editorStore";
import { api } from "../../convex/_generated/api";
import { generateClientThumbnails, uploadClientThumbnails } from "./clientThumbnailGenerator";
import { resolveStoragePathSegments, type StoragePathSegments } from "@/utils/storagePaths";

/**
 * Check if a panorama is already compressed based on its file URLs
 */
const isAlreadyCompressed = (panorama: Panorama): boolean => {
  // Check if the private_file_url contains compressed_ indicating it's already processed
  const hasCompressedUrl = panorama.private_file_url?.includes("compressed_");

  // Check if the file_url contains compressed_ (for public files)
  const hasCompressedPublicUrl = panorama.file_url?.includes("compressed_");

  // Check if the panorama name itself indicates it's compressed
  const hasCompressedName = panorama.name?.includes("compressed_");

  // Check if the data field indicates it's already compressed
  const isMarkedAsCompressed = panorama.data?.is_compressed === true;

  return hasCompressedUrl || hasCompressedPublicUrl || hasCompressedName || isMarkedAsCompressed;
};

/**
 * Check if a panorama actually needs reprocessing
 */
const needsReprocessing = (panorama: Panorama): boolean => {
  // Don't reprocess if already compressed
  if (isAlreadyCompressed(panorama)) {
    console.log(`📋 Skipping ${panorama.name} - already compressed`);
    return false;
  }

  // Don't reprocess if completed and has thumbnails
  if (panorama.processing_status === "completed" && panorama.thumbnail_url) {
    console.log(`📋 Skipping ${panorama.name} - already completed with thumbnails`);
    return false;
  }

  // Reprocess if missing thumbnails or failed/pending status
  const needsProcessing =
    !panorama.thumbnail_url ||
    panorama.processing_status === "failed" ||
    panorama.processing_status === "pending";

  if (needsProcessing) {
    console.log(
      `🔄 ${panorama.name} needs reprocessing - status: ${panorama.processing_status}, has thumbnail: ${!!panorama.thumbnail_url}`,
    );
  }

  return needsProcessing;
};

/**
 * Utility function to reprocess panoramas that don't have thumbnails
 * This can be used to fix existing panoramas that failed processing
 * Now uses client-side processing instead of edge functions
 * Improved to skip already compressed panoramas
 * @param specificPanoramaId Optional: if provided, only process this specific panorama
 */
export const reprocessPanoramas = async (specificPanoramaId?: string) => {
  try {
    const allPanoramas = (await convex.query(api.panoramas.listMine, {})) as Panorama[] | null;

    const candidatePanoramas = (allPanoramas || []).filter((panorama) => {
      if (!panorama?.private_file_url) return false;
      if (specificPanoramaId && panorama.id !== specificPanoramaId) return false;
      return (
        !panorama.thumbnail_url ||
        panorama.processing_status === "failed" ||
        panorama.processing_status === "pending"
      );
    });

    if (!candidatePanoramas || candidatePanoramas.length === 0) {
      return {
        success: true,
        message: specificPanoramaId
          ? "Panorama not found or already processed"
          : "No panoramas need reprocessing",
      };
    }

    // Filter out panoramas that don't actually need reprocessing
    const panoramas = candidatePanoramas.filter(needsReprocessing);

    if (panoramas.length === 0) {
      const skippedCount = allPanoramas.length;
      console.log(
        `✅ All ${skippedCount} panoramas are already processed (${skippedCount} compressed or completed)`,
      );
      return {
        success: true,
        message: `All panoramas are already processed. Skipped ${skippedCount} compressed/completed panoramas.`,
        summary: {
          total: allPanoramas.length,
          successful: 0,
          failed: 0,
          skipped: skippedCount,
          results: [],
        },
      };
    }

    console.log(
      `🔄 Processing ${panoramas.length} panoramas (skipped ${allPanoramas.length - panoramas.length} already compressed/completed)`,
    );

    const results = [];
    const craftspaceByProject = new Map<string, string>();

    // Process each panorama
    for (const panorama of panoramas) {
      try {
        console.log(`Reprocessing panorama: ${panorama.name}`);

        // Update status to processing
        await convex.mutation(api.panoramas.update, {
          id: panorama.id,
          processing_status: "processing",
        });

        // Fetch the original image
        const imageResponse = await fetch(panorama.private_file_url);
        if (!imageResponse.ok) {
          throw new Error(`Failed to fetch image: ${imageResponse.status}`);
        }

        // Convert response to File object for thumbnail generation
        const imageBlob = await imageResponse.blob();
        const file = new File([imageBlob], panorama.name, { type: imageBlob.type });

        // Generate thumbnails client-side
        const thumbnails = await generateClientThumbnails(file);

        // Get craftspace ID from project
        let craftspaceId = craftspaceByProject.get(panorama.project_id);
        let storageSegments: StoragePathSegments | undefined;
        if (!craftspaceId) {
          try {
            const project = await convex.query(api.projects.get, { id: panorama.project_id });
            craftspaceId = project?.craftspace_id || "default";
            const projectData = project?.data as Record<string, unknown> | null;
            if (project) {
              storageSegments = resolveStoragePathSegments({
                craftspaceId,
                projectId: project.id,
                projectName: project.name,
                projectData,
                storagePath:
                  typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
              });
            }
          } catch {
            craftspaceId = "default";
          }
          craftspaceByProject.set(panorama.project_id, craftspaceId);
        }
        const panoramaName = panorama.name.replace(/[^a-zA-Z0-9-_]/g, "_").toLowerCase();

        // Upload thumbnails
        const thumbnailUrls = await uploadClientThumbnails(
          thumbnails,
          craftspaceId,
          panorama.project_id,
          panoramaName,
          storageSegments,
        );

        // Update panorama record
        await convex.mutation(api.panoramas.update, {
          id: panorama.id,
          thumbnail_url: thumbnailUrls.thumbnail_url,
          thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
          processing_status: "completed",
        });

        results.push({ id: panorama.id, success: true });
        console.log(`Successfully reprocessed: ${panorama.name}`);
      } catch (panoramaError) {
        console.error(`Failed to reprocess panorama ${panorama.name}:`, panoramaError);

        // Update status to failed
        await convex.mutation(api.panoramas.update, {
          id: panorama.id,
          processing_status: "failed",
        });

        results.push({
          id: panorama.id,
          success: false,
          error: panoramaError instanceof Error ? panoramaError.message : "Unknown error",
        });
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.filter((r) => !r.success).length;
    const skippedCount = allPanoramas.length - panoramas.length;

    return {
      success: true,
      summary: {
        total: allPanoramas.length,
        processed: panoramas.length,
        successful: successCount,
        failed: failCount,
        skipped: skippedCount,
        results,
      },
    };
  } catch (error) {
    console.error("Error in reprocessPanoramas:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

/**
 * Fix already compressed panoramas that might have failed status
 * This function finds compressed panoramas with failed/pending status and marks them as completed
 */
export const fixCompressedPanoramasStatus = async () => {
  try {
    console.log("🔍 Checking for compressed panoramas with incorrect status...");

    const panoramas =
      ((await convex.query(api.panoramas.listMine, {})) as Panorama[] | null)?.filter(
        (panorama) =>
          !!panorama?.private_file_url &&
          (panorama.processing_status === "failed" || panorama.processing_status === "pending"),
      ) || [];

    if (!panoramas || panoramas.length === 0) {
      return { success: true, message: "No panoramas with failed/pending status found" };
    }

    // Find compressed panoramas that just need status updates
    const compressedPanoramas = panoramas.filter(isAlreadyCompressed);

    if (compressedPanoramas.length === 0) {
      console.log("✅ No compressed panoramas found with incorrect status");
      return { success: true, message: "No compressed panoramas need status fixes" };
    }

    console.log(
      `🔧 Found ${compressedPanoramas.length} compressed panoramas with incorrect status, fixing...`,
    );

    const results = [];

    // Update status for compressed panoramas
    for (const panorama of compressedPanoramas) {
      try {
        await convex.mutation(api.panoramas.update, {
          id: panorama.id,
          processing_status: "completed",
          file_status: "public", // Ensure file_status is also correct
        });

        results.push({ id: panorama.id, name: panorama.name, success: true });
        console.log(`✅ Fixed status for compressed panorama: ${panorama.name}`);
      } catch (panoramaError) {
        console.error(`❌ Failed to fix ${panorama.name}:`, panoramaError);
        results.push({
          id: panorama.id,
          name: panorama.name,
          success: false,
          error: panoramaError instanceof Error ? panoramaError.message : "Unknown error",
        });
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.filter((r) => !r.success).length;

    return {
      success: true,
      message: `Fixed ${successCount} compressed panoramas${failCount > 0 ? `, ${failCount} failed` : ""}`,
      summary: {
        total: compressedPanoramas.length,
        successful: successCount,
        failed: failCount,
        results,
      },
    };
  } catch (error) {
    console.error("Error fixing compressed panoramas status:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

/**
 * Check panorama processing status
 */
export const checkPanoramaStatus = async () => {
  try {
    const panoramas =
      ((await convex.query(api.panoramas.listMine, {})) as Panorama[] | null)?.filter(
        (panorama) => !!panorama?.private_file_url,
      ) || [];

    const status = {
      total: panoramas?.length || 0,
      pending: panoramas?.filter((p) => p.processing_status === "pending").length || 0,
      processing: panoramas?.filter((p) => p.processing_status === "processing").length || 0,
      completed: panoramas?.filter((p) => p.processing_status === "completed").length || 0,
      failed: panoramas?.filter((p) => p.processing_status === "failed").length || 0,
      noThumbnail: panoramas?.filter((p) => !p.thumbnail_url).length || 0,
    };

    // Panorama processing status logged
    return { success: true, status };
  } catch (error) {
    console.error("Error checking panorama status:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

// Make functions available globally for browser console access
// (placed at end to avoid initialization order issues)
if (typeof window !== "undefined") {
  const typedWindow = window as Window & {
    reprocessPanoramas?: typeof reprocessPanoramas;
    fixCompressedPanoramasStatus?: typeof fixCompressedPanoramasStatus;
    checkPanoramaStatus?: typeof checkPanoramaStatus;
  };
  typedWindow.reprocessPanoramas = reprocessPanoramas;
  typedWindow.fixCompressedPanoramasStatus = fixCompressedPanoramasStatus;
  typedWindow.checkPanoramaStatus = checkPanoramaStatus;
}
